import React, { useEffect, useState, useRef } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const autoScrollIntervalRef = useRef(null);
  const thumbnailContainerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // ตรวจสอบว่า id เป็น UUID หรือ integer
        // ถ้าเป็น UUID จะมีรูปแบบ xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
        const queryId = isUUID ? id : (isNaN(Number(id)) ? id : Number(id));
        
        const { data, error } = await supabase
          .from('news')
          .select('id, title, content, category, image_url, media_urls, publish_date, created_at')
          .eq('id', queryId)
          .maybeSingle();
        if (error) throw error;
        if (mounted) {
          if (data) {
            // แปลง media_urls จาก JSON string เป็น array
            let mediaUrls = [];
            if (data.media_urls) {
              try {
                mediaUrls = typeof data.media_urls === 'string' 
                  ? JSON.parse(data.media_urls) 
                  : (Array.isArray(data.media_urls) ? data.media_urls : []);
              } catch (e) {
                mediaUrls = [];
              }
            }
            
            setNews({
              id: data.id,
              title: data.title,
              content: data.content || '',
              category: data.category || 'ข่าวสาร',
              date: (data.publish_date || data.created_at),
              image: data.image_url || '',
              mediaUrls: mediaUrls,
              status: 'ปกติ',
            });
          } else {
            setNews(null);
          }
        }
      } catch (e) {
        if (mounted) setError(e.message || 'เกิดข้อผิดพลาดในการโหลดข่าว');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id]);

  // Auto-scroll สำหรับรูปภาพหลายรูป
  useEffect(() => {
    if (!news) {
      setCurrentImageIndex(0);
      return;
    }
    
    try {
      // หารูปภาพทั้งหมด
      const allImages = [];
      if (news.image) {
        allImages.push({ url: news.image, type: 'image', name: news.title });
      }
      if (news.mediaUrls && Array.isArray(news.mediaUrls) && news.mediaUrls.length > 0) {
        const imageUrls = news.mediaUrls.filter(m => m && m.type === 'image' && m.url);
        allImages.push(...imageUrls);
      }
      
      // Reset index เมื่อข้อมูลเปลี่ยน
      setCurrentImageIndex(0);
      
      // ถ้ามีรูปภาพมากกว่า 1 รูป ให้เริ่ม auto-scroll
      if (allImages.length > 1) {
        // Clear interval เดิมก่อน
        if (autoScrollIntervalRef.current) {
          clearInterval(autoScrollIntervalRef.current);
        }
        
        autoScrollIntervalRef.current = setInterval(() => {
          setCurrentImageIndex((prev) => {
            const nextIndex = (prev + 1) % allImages.length;
            return nextIndex;
          });
        }, 4000); // เปลี่ยนรูปทุก 4 วินาที
      } else {
        // Clear interval ถ้ามีรูปเดียว
        if (autoScrollIntervalRef.current) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }
      }
    } catch (e) {
      console.error('Error in auto-scroll effect:', e);
      setCurrentImageIndex(0);
    }
    
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };
  }, [news]);

  // Auto-scroll thumbnail container ไปยังรูปที่กำลังแสดง
  useEffect(() => {
    if (thumbnailContainerRef.current && currentImageIndex >= 0) {
      const thumbnailWidth = 120; // width ของ thumbnail
      const gap = 12; // gap ระหว่าง thumbnails
      const scrollPosition = currentImageIndex * (thumbnailWidth + gap) - (thumbnailContainerRef.current.offsetWidth / 2) + (thumbnailWidth / 2);
      
      thumbnailContainerRef.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }, [currentImageIndex]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center text-muted-foreground">กำลังโหลด...</div>
        </div>
      </div>
    );
  }

  if (!news || error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">ไม่พบข่าวที่ต้องการ</h1>
            <button 
              onClick={() => navigate('/news-and-announcements')}
              className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              กลับหน้าหลัก
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Breadcrumb Navigation */}
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <BreadcrumbTrail />
        </div>

        {/* News Detail Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/news-and-announcements')}
            className="flex items-center text-primary hover:text-primary/80 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับหน้าหลัก
          </button>

          {/* News Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* หัวข้อข่าว */}
            <div className="p-6 md:p-8 border-b border-gray-200">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {news.title}
              </h1>
              
              {/* Category and Date */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {news.category}
                </span>
                <span className="text-gray-500 text-sm">
                  {new Date(news.date).toLocaleDateString('th-TH', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
            </div>

            {/* รูปภาพหลักขนาดใหญ่ + Carousel */}
            {(() => {
              // หารูปภาพทั้งหมด
              const allImages = [];
              if (news.image) {
                allImages.push({ url: news.image, type: 'image', name: news.title });
              }
              if (news.mediaUrls && Array.isArray(news.mediaUrls) && news.mediaUrls.length > 0) {
                const imageUrls = news.mediaUrls.filter(m => m && m.type === 'image' && m.url);
                allImages.push(...imageUrls);
              }
              
              if (allImages.length === 0) {
                return null;
              }
              
              // ตรวจสอบว่า currentImageIndex อยู่ใน range
              const safeIndex = Math.max(0, Math.min(currentImageIndex || 0, allImages.length - 1));
              const currentImage = allImages[safeIndex];
              
              if (!currentImage || !currentImage.url) {
                // Fallback: แสดงรูปแรก
                const firstImage = allImages[0];
                if (!firstImage || !firstImage.url) {
                  return null;
                }
                return (
                  <div className="bg-gray-50 p-4 md:p-6">
                    <div className="relative w-full mb-6 overflow-hidden rounded-lg bg-white shadow-md">
                      <img 
                        src={firstImage.url} 
                        alt={firstImage.name || news.title}
                        className="w-full h-auto object-contain max-h-[600px] mx-auto"
                        loading="lazy"
                      />
                    </div>
                  </div>
                );
              }
              
              try {
                return (
                <div className="bg-gray-50 p-4 md:p-6">
                  {/* แสดงรูปภาพหลักขนาดใหญ่ (Auto-scroll) */}
                  <div className="relative w-full mb-6 overflow-hidden rounded-lg bg-white shadow-md group">
                    {allImages.length > 1 ? (
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                          key={safeIndex}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.5 }}
                          className="relative w-full"
                        >
                          <img 
                            src={currentImage.url} 
                            alt={currentImage.name || news.title}
                            className="w-full h-auto object-contain max-h-[600px] mx-auto"
                            loading="lazy"
                            onError={(e) => {
                              console.error('Image load error:', currentImage.url);
                              e.target.style.display = 'none';
                            }}
                          />
                        </motion.div>
                      </AnimatePresence>
                    ) : (
                      <div className="relative w-full">
                        <img 
                          src={currentImage.url} 
                          alt={currentImage.name || news.title}
                          className="w-full h-auto object-contain max-h-[600px] mx-auto"
                          loading="lazy"
                          onError={(e) => {
                            console.error('Image load error:', currentImage.url);
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Navigation arrows (ถ้ามีมากกว่า 1 รูป) */}
                    {allImages.length > 1 && (
                      <>
                        <button
                          onClick={() => {
                            setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
                            // Reset auto-scroll timer
                            if (autoScrollIntervalRef.current) {
                              clearInterval(autoScrollIntervalRef.current);
                            }
                            autoScrollIntervalRef.current = setInterval(() => {
                              setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                            }, 4000);
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                            // Reset auto-scroll timer
                            if (autoScrollIntervalRef.current) {
                              clearInterval(autoScrollIntervalRef.current);
                            }
                            autoScrollIntervalRef.current = setInterval(() => {
                              setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                            }, 4000);
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* แสดงรูปภาพ thumbnail หลายรูปด้านล่าง (Auto-scroll) */}
                  {allImages.length > 1 && (
                    <div className="relative mt-4">
                      <div ref={thumbnailContainerRef} className="overflow-x-auto scrollbar-hide">
                        <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
                          {allImages.map((media, index) => (
                            <motion.div
                              key={index}
                              className={`relative aspect-video overflow-hidden rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 ${
                                index === currentImageIndex
                                  ? 'border-blue-500 shadow-lg scale-105'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ width: '120px' }}
                              whileHover={{ scale: 1.05 }}
                              onClick={() => {
                                setCurrentImageIndex(index);
                                // Reset auto-scroll timer
                                if (autoScrollIntervalRef.current) {
                                  clearInterval(autoScrollIntervalRef.current);
                                }
                                autoScrollIntervalRef.current = setInterval(() => {
                                  setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                                }, 4000);
                              }}
                            >
                              <img 
                                src={media.url} 
                                alt={media.name || news.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              {index === currentImageIndex && (
                                <div className="absolute inset-0 bg-blue-500/20"></div>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                );
              } catch (e) {
                console.error('Error rendering image carousel:', e);
                // Fallback: แสดงรูปภาพหลักถ้ามี
                if (currentImage && currentImage.url) {
                  return (
                    <div className="bg-gray-50 p-4 md:p-6">
                      <div className="relative w-full mb-6 overflow-hidden rounded-lg bg-white shadow-md">
                        <img 
                          src={currentImage.url} 
                          alt={currentImage.name || news.title}
                          className="w-full h-auto object-contain max-h-[600px] mx-auto"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  );
                }
                return null;
              }
            })()}

            {/* News Content */}
            <div className="p-6 md:p-8">
              {/* Content */}
              {news.content ? (
                <div 
                  className="text-gray-700 leading-relaxed text-base md:text-lg whitespace-pre-wrap prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: news.content }}
                />
              ) : (
                <div className="text-gray-500 text-base md:text-lg italic">
                  ไม่มีเนื้อหาข่าว
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewsDetail;
